import { useEffect, useState, createContext, useContext, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { UnsavedChangesProvider } from "@/components/UnsavedChangesGuard";

// Pages
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Bienvenida from "@/pages/Bienvenida";
import NewQuote from "@/pages/NewQuote";
import EditQuote from "@/pages/EditQuote";
import QuotePreview from "@/pages/QuotePreview";
import QuoteHistory from "@/pages/QuoteHistory";
import Settings from "@/pages/Settings";
import SettingsAgencia from "@/pages/SettingsAgencia";
import SettingsOpciones from "@/pages/SettingsOpciones";
import SettingsAeropuertos from "@/pages/SettingsAeropuertos";
import SettingsDestinos from "@/pages/SettingsDestinos";
import SettingsAerolineas from "@/pages/SettingsAerolineas";
import SettingsHoteles from "@/pages/SettingsHoteles";
import SettingsProveedores from "@/pages/SettingsProveedores";
import SettingsArticulos from "@/pages/SettingsArticulos";
import TablasCentrosTrabajo from "@/pages/TablasCentrosTrabajo";
import TablasDepartamentosOperativos from "@/pages/TablasDepartamentosOperativos";
import TablasCategoriasTickets from "@/pages/TablasCategoriasTickets";
import TablasEmpleados from "@/pages/TablasEmpleados";
import ERPFacturacion from "@/pages/ERPFacturacion";
import ERPVinculacion from "@/pages/ERPVinculacion";
import Vehiculos from "@/pages/Vehiculos";
import VehiculoDetalle from "@/pages/VehiculoDetalle";
import UserManagement from "@/pages/UserManagement";
import NewClient from "@/pages/NewClient";
import ClientCRM from "@/pages/ClientCRM";
import DirectorioContactos from "@/pages/DirectorioContactos";
import ClientDetail from "@/pages/ClientDetail";
import Logistics from "@/pages/Logistics";
import Inventarios from "@/pages/Inventarios";
import Pedidos from "@/pages/Pedidos";
import FixedAssets from "@/pages/FixedAssets";
import TraveldeskCalculadora from "@/pages/TraveldeskCalculadora";
import TraveldeskItinerarios from "@/pages/TraveldeskItinerarios";
import TraveldeskDashboardServicios from "@/pages/TraveldeskDashboardServicios";
import ViajeroInterno from "@/pages/ViajeroInterno";
import ViajeroExterno from "@/pages/ViajeroExterno";
import DashboardComercial from "@/pages/DashboardComercial";
import CabinaVentas from "@/pages/CabinaVentas";
import CabinaExpediente from "@/pages/CabinaExpediente";
import CabinaTableroCostos from "@/pages/CabinaTableroCostos";
import CabinaMatrizFiscal from "@/pages/CabinaMatrizFiscal";
import CabinaLaboratorio from "@/pages/CabinaLaboratorio";
import CabinaPendientes from "@/pages/CabinaPendientes";
import AdminFacturacionGlobal from "@/pages/AdminFacturacionGlobal";
import AdminPagosGlobales from "@/pages/AdminPagosGlobales";
import AdminMonedasConfig from "@/pages/AdminMonedasConfig";
import AdminMonedasTC from "@/pages/AdminMonedasTC";
import ViajeroExternoPublico from "@/pages/ViajeroExternoPublico";
import MiPerfilViajero from "@/pages/MiPerfilViajero";
import CotizadorIframe from "@/pages/CotizadorIframe";
import HelpdeskTickets from "@/pages/HelpdeskTickets";
import HelpdeskTicketDetail from "@/pages/HelpdeskTicketDetail";
import HelpdeskContactos from "@/pages/HelpdeskContactos";
import HelpdeskContactoDetalle from "@/pages/HelpdeskContactoDetalle";
import HelpdeskMetricas from "@/pages/HelpdeskMetricas";
import QuickReplies from "@/pages/QuickReplies";
import PaginasPublicas from "@/pages/PaginasPublicas";
import PublicPage from "@/pages/PublicPage";
import TicketExternoPublico from "@/pages/TicketExternoPublico";
import EncuestaCalidad from "@/pages/EncuestaCalidad";
import FormulariosAccesos from "@/pages/FormulariosAccesos";
import FormulariosServicios from "@/pages/FormulariosServicios";
import FormulariosBTravelConsola from "@/pages/FormulariosBTravelConsola";
import ClientePortal from "@/pages/ClientePortal";
import EncuestaPublica from "@/pages/EncuestaPublica";
import EncuestasEventos from "@/pages/EncuestasEventos";
import EncuestasDashboard from "@/pages/EncuestasDashboard";
import TopMenuBar from "@/components/TopMenuBar";
import { NotificationProvider } from "@/components/NotificationProvider";

// Use the same origin for API calls when on a custom domain
// This ensures CORS works correctly with credentials
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  const currentOrigin = window.location.origin;
  
  // If we're on a custom domain (not preview or localhost), use the same origin
  if (!currentOrigin.includes('preview.emergentagent.com') && 
      !currentOrigin.includes('localhost') &&
      !currentOrigin.includes('127.0.0.1')) {
    return currentOrigin;
  }
  
  return envUrl;
};

const BACKEND_URL = getApiBaseUrl();
export const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies
axios.defaults.withCredentials = true;

// Auth Context
const AuthContext = createContext(null);

// Sidebar Context
const SidebarContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within SidebarProvider");
  }
  return context;
};

// Auth Provider with Google OAuth
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  const setAuthUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginWithGoogle, logout, setAuthUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component - handles session_id exchange
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setAuthUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      console.log("Auth callback - hash:", hash);
      console.log("Auth callback - API URL:", API);
      
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        console.log("Auth callback - no session_id found in hash");
        navigate('/login');
        return;
      }

      const sessionId = sessionIdMatch[1];
      console.log("Auth callback - session_id extracted:", sessionId.substring(0, 20) + "...");

      try {
        console.log("Auth callback - calling /api/auth/session...");
        const response = await axios.post(`${API}/auth/session`, 
          { session_id: sessionId },
          { withCredentials: true }
        );
        
        console.log("Auth callback - success, user:", response.data.user?.name);
        setAuthUser(response.data.user);
        toast.success(`¡Bienvenido, ${response.data.user.name}!`);
        
        // Clear hash and navigate to dashboard
        window.history.replaceState(null, '', '/');
        navigate('/', { replace: true, state: { user: response.data.user } });
      } catch (error) {
        console.error("Auth error details:", {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
          headers: error.response?.headers
        });
        const detail = error.response?.data?.detail || "";
        
        // Handle pending account or deactivated account
        if (detail.startsWith("CUENTA_PENDIENTE|")) {
          const msg = detail.split("|")[1];
          window.history.replaceState(null, '', '/login');
          navigate('/login', { replace: true, state: { pendingMessage: msg } });
        } else if (detail.startsWith("CUENTA_DESACTIVADA|")) {
          const msg = detail.split("|")[1];
          window.history.replaceState(null, '', '/login');
          navigate('/login', { replace: true, state: { errorMessage: msg } });
        } else {
          toast.error(detail || "Error de autenticación");
          navigate('/login');
        }
      }
    };

    processSession();
  }, [navigate, setAuthUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
        <p className="text-muted-foreground">Autenticando...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // If user data was passed from AuthCallback, use it
  if (location.state?.user && !user) {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Layout with Sidebar
const AppLayout = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { user } = useAuth();

  return (
    <SidebarContext.Provider value={{ isCollapsed, setIsCollapsed }}>
      <NotificationProvider user={user}>
        <div className="app-layout-top">
          <TopMenuBar />
          <main className="main-content-top bg-background">
            {children}
          </main>
        </div>
      </NotificationProvider>
    </SidebarContext.Provider>
  );
};

// App Router - detects session_id before rendering routes
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id - MUST be synchronous (before ProtectedRoute runs)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/soporte"
        element={
          <ProtectedRoute>
            <AppLayout>
              <TicketExternoPublico />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/encuesta/:id" element={<EncuestaCalidad />} />
      <Route path="/cliente/:slug" element={<ClientePortal />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Bienvenida />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Dashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/nueva-cotizacion"
        element={
          <ProtectedRoute>
            <AppLayout>
              <NewQuote />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/editar/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <EditQuote />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cotizacion/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <QuotePreview />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/historial"
        element={
          <ProtectedRoute>
            <AppLayout>
              <QuoteHistory />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracion"
        element={<Navigate to="/configuracion/agencia" replace />}
      />
      <Route
        path="/configuracion/agencia"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SettingsAgencia />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracion/opciones"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SettingsOpciones />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracion/tablas/aeropuertos"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SettingsAeropuertos />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracion/tablas/destinos"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SettingsDestinos />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracion/tablas/aerolineas"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SettingsAerolineas />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracion/tablas/hoteles"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SettingsHoteles />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracion/tablas/proveedores"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SettingsProveedores />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracion/tablas/articulos"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SettingsArticulos />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracion/tablas/centros-trabajo"
        element={
          <ProtectedRoute>
            <AppLayout>
              <TablasCentrosTrabajo />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracion/tablas/empleados"
        element={
          <ProtectedRoute>
            <AppLayout>
              <TablasEmpleados />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracion/tablas/departamentos-operativos"
        element={
          <ProtectedRoute>
            <AppLayout>
              <TablasDepartamentosOperativos />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracion/tablas/categorias-tickets"
        element={
          <ProtectedRoute>
            <AppLayout>
              <TablasCategoriasTickets />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/erp/facturacion"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ERPFacturacion />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/erp/vinculacion"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ERPVinculacion />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/usuarios"
        element={
          <ProtectedRoute>
            <AppLayout>
              <UserManagement />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientes/nuevo"
        element={
          <ProtectedRoute>
            <AppLayout>
              <NewClient />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientes/crm"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ClientCRM />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientes/encuestas"
        element={
          <ProtectedRoute>
            <AppLayout>
              <EncuestasEventos />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientes/encuestas/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout>
              <EncuestasDashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientes/directorio-contactos"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DirectorioContactos />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Formulario público de encuestas de calidad — sin auth */}
      <Route path="/eq/:slug" element={<EncuestaPublica />} />
      <Route
        path="/clientes/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ClientDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/logistica"
        element={<Navigate to="/logistica/productos" replace />}
      />
      <Route
        path="/logistica/productos"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Logistics />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/logistica/inventarios"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Inventarios />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/logistica/pedidos"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Pedidos />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/logistica/vehiculos"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Vehiculos />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/logistica/vehiculos/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <VehiculoDetalle />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/helpdesk/tickets"
        element={
          <ProtectedRoute>
            <AppLayout>
              <HelpdeskTickets />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/helpdesk/tickets/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <HelpdeskTicketDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/helpdesk/contactos"
        element={
          <ProtectedRoute>
            <AppLayout>
              <HelpdeskContactos />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/helpdesk/contactos/:contactoId"
        element={
          <ProtectedRoute>
            <AppLayout>
              <HelpdeskContactoDetalle />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/helpdesk/metricas"
        element={
          <ProtectedRoute>
            <AppLayout>
              <HelpdeskMetricas />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/helpdesk/respuestas-rapidas"
        element={
          <ProtectedRoute>
            <AppLayout>
              <QuickReplies />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Public page renderer — no auth */}
      <Route path="/p/:slug" element={<PublicPage />} />
      <Route
        path="/configuracion/paginas-publicas"
        element={
          <ProtectedRoute>
            <AppLayout>
              <PaginasPublicas />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/comercial/tabla-cotizadora"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CotizadorIframe />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/traveldesk/calculadora"
        element={
          <ProtectedRoute>
            <AppLayout>
              <TraveldeskCalculadora />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/traveldesk/itinerarios"
        element={
          <ProtectedRoute>
            <AppLayout>
              <TraveldeskItinerarios />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/viajero/:slug" element={<ViajeroExternoPublico />} />
      <Route path="/viajero/:slug/:subslug" element={<ViajeroExternoPublico />} />
      <Route
        path="/traveldesk/perfil-viajero/externo"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ViajeroExterno />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/traveldesk/perfil-viajero/interno"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ViajeroInterno />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mi-perfil-viajero"
        element={
          <ProtectedRoute>
            <AppLayout>
              <MiPerfilViajero />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard-comercial"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DashboardComercial />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/traveldesk/dashboard-servicios"
        element={
          <ProtectedRoute>
            <AppLayout>
              <TraveldeskDashboardServicios />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cabina-ventas"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CabinaVentas />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cabina-ventas/expediente/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CabinaExpediente />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ventas/tablero-costos"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CabinaTableroCostos />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ventas/matriz-fiscal"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CabinaMatrizFiscal />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracion/ventas/laboratorio"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CabinaLaboratorio />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ventas/pendientes"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CabinaPendientes />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/facturacion-global"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AdminFacturacionGlobal />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/pagos-globales"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AdminPagosGlobales />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/monedas/configuracion"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AdminMonedasConfig />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/monedas/tipos-cambio"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AdminMonedasTC />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/activos-fijos"
        element={
          <ProtectedRoute>
            <AppLayout>
              <FixedAssets />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/formularios-clientes/servicios"
        element={
          <ProtectedRoute>
            <AppLayout>
              <FormulariosServicios />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/formularios-clientes/accesos"
        element={
          <ProtectedRoute>
            <AppLayout>
              <FormulariosAccesos />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/formularios-clientes/consola"
        element={
          <ProtectedRoute>
            <AppLayout>
              <FormulariosBTravelConsola />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <UnsavedChangesProvider>
          <AppRouter />
        </UnsavedChangesProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
    </AuthProvider>
  );
}

export default App;
